<template>
	<Route>
		<template slot="title">
			Search
		</template>
		<v-container
			grid-list-xl
			my-2
		>
			<v-layout
				row
				wrap
				justify-center
			>
				<v-flex
					xs12
					xl8
					pb-4
				>
					<v-card>
						<v-progress-linear
							v-if="articlesSearch.loading"
							class="ma-0 absolute--loader"
							indeterminate
						/>
						<v-container>
							<v-layout
								row
								wrap
							>
								<v-flex
									xs12
									pb-0
								>
									<div class="title">
										Filter by:
										<v-tooltip right>
											<span>Reset</span>
											<v-btn
												slot="activator"
												icon
												flat
												dark
												small
												color="secondary"
												class="my-0"
												@click="resetAll"
											>
												<v-icon>replay</v-icon>
											</v-btn>
										</v-tooltip>
									</div>
								</v-flex>
								<v-flex
									xs12
									md6
								>
									<v-autocomplete
										ref="projectSearch"
										v-model="selectedProjects"
										:items="projects"
										:filter="projectsFilter"
										chips
										label="Projects"
										item-text="name"
										item-value="id"
										multiple
										hide-details
										@change="updateParams"
									>
										<template
											slot="selection"
											slot-scope="data"
										>
											<v-chip
												close
												class="chip--select-multi"
												color="secondary white--text"
												@input="remove(data.item, selectedProjects)"
											>
												{{ data.item.name }}
											</v-chip>
										</template>
										<template
											slot="item"
											slot-scope="data"
										>
											<v-list-tile-content>{{ data.item.name }}</v-list-tile-content>
										</template>
									</v-autocomplete>
								</v-flex>
								<v-flex
									xs12
									md6
								>
									<v-autocomplete
										ref="reportSearch"
										v-model="selectedReports"
										:items="reports"
										:filter="reportsFilter"
										:loading="reportsLoading"
										:no-data-text="reportsNoDataText"
										chips
										label="Reports"
										item-text="displayName"
										item-value="id"
										multiple
										hide-details
										@change="updateParams"
									>
										<template
											slot="selection"
											slot-scope="data"
										>
											<v-chip
												close
												class="chip--select-multi"
												color="secondary white--text"
												@input="remove(data.item, selectedReports)"
											>
												{{ data.item.displayName }}
											</v-chip>
										</template>
										<template
											slot="item"
											slot-scope="data"
										>
											<v-list-tile-content>
												<div>
													{{ data.item.displayName }}
													<span
														v-if="data.item.date"
														class="caption"
													>
														- {{ data.item.date }}
													</span>
												</div>
											</v-list-tile-content>
										</template>
									</v-autocomplete>
								</v-flex>
								<v-flex
									xs12
									md6
								>
									<v-autocomplete
										ref="userSearch"
										v-model="selectedUsers"
										:items="users.models"
										:filter="usersFilter"
										chips
										label="Users"
										item-text="realName"
										item-value="id"
										no-data-text="No users found."
										multiple
										hide-details
										@change="updateParams"
									>
										<template
											slot="selection"
											slot-scope="data"
										>
											<v-chip
												close
												class="chip--select-multi"
												color="secondary white--text"
												@input="remove(data.item, selectedUsers)"
											>
												{{ data.item.realName }}
											</v-chip>
										</template>
										<template
											slot="item"
											slot-scope="data"
										>
											<v-list-tile-content>{{ data.item.realName }}</v-list-tile-content>
										</template>
									</v-autocomplete>
								</v-flex>
								<v-flex
									xs12
									md6
								>
									<v-autocomplete
										ref="categorySearch"
										v-model="selectedCategories"
										:items="categories"
										:filter="projectsFilter"
										:loading="categoriesLoading"
										:no-data-text="categoriesNoDataText"
										chips
										label="Categories"
										item-text="name"
										item-value="id"
										multiple
										hide-details
										@change="updateParams"
									>
										<template
											slot="selection"
											slot-scope="data"
										>
											<v-chip
												close
												class="chip--select-multi"
												color="secondary white--text"
												@input="remove(data.item, selectedCategories)"
											>
												{{ data.item.name }}
											</v-chip>
										</template>
										<template
											slot="item"
											slot-scope="data"
										>
											<v-list-tile-content>
												<div>
													{{ data.item.name }}
													<span
														v-if="data.item.isArchived"
														class="caption"
													>
														- Archived
													</span>
												</div>
											</v-list-tile-content>
										</template>
									</v-autocomplete>
								</v-flex>
								<v-flex
									xs12
									md6
								>
									<v-autocomplete
										ref="publicationSearch"
										v-model="selectedPublications"
										:items="publicationsItems"
										:filter="projectsFilter"
										:loading="publications.loading"
										:no-data-text="publicationsNoDataText"
										:search-input.sync="publicationSearchInput"
										chips
										label="Publications"
										item-text="name"
										item-value="id"
										multiple
										hide-details
										@change="updateParams"
									>
										<template
											slot="selection"
											slot-scope="data"
										>
											<v-chip
												close
												class="chip--select-multi"
												color="secondary white--text"
												@input="remove(data.item, selectedPublications)"
											>
												{{ data.item.name }}
											</v-chip>
										</template>
										<template
											slot="item"
											slot-scope="data"
										>
											<v-list-tile-content>
												<div>
													{{ data.item.name }}
												</div>
											</v-list-tile-content>
										</template>
									</v-autocomplete>
								</v-flex>
								<v-flex
									xs6
									md3
								>
									<v-menu
										v-model="showStartDate"
										:close-on-content-click="false"
										lazy
										transition="scale-transition"
										offset-y
										full-width
										max-width="290px"
										min-width="290px"
									>
										<v-text-field
											slot="activator"
											v-model="startDate"
											append-outer-icon="clear"
											label="Start Date"
											readonly
											hide-details
											@click:append-outer="resetStartDate()"
										/>
										<v-date-picker
											v-model="startDate"
											show-current
											no-title
											@input="showStartDate = !showStartDate"
											@change="updateParams"
										/>
									</v-menu>
								</v-flex>
								<v-flex
									xs6
									md3
								>
									<v-menu
										v-model="showEndDate"
										:close-on-content-click="false"
										lazy
										transition="scale-transition"
										offset-y
										full-width
										max-width="290px"
										min-width="290px"
									>
										<v-text-field
											slot="activator"
											v-model="endDate"
											append-outer-icon="clear"
											label="End Date"
											readonly
											hide-details
											@click:append-outer="resetEndDate()"
										/>
										<v-date-picker
											v-model="endDate"
											:min="startDate"
											show-current
											no-title
											@input="showEndDate = !showEndDate"
											@change="updateParams"
										/>
									</v-menu>
								</v-flex>
								<!-- <v-flex>
									<v-switch
										v-model="hasIssues"
										hide-details
										color="secondary"
										label="Issue Articles"
										@change="updateParams"
									/>
								</v-flex> -->
								<v-flex>
									<v-switch
										v-model="isDeleted"
										hide-details
										color="secondary"
										label="Deleted Articles"
										@change="updateParams"
									/>
								</v-flex>
								<v-flex xs12>
									<v-text-field
										v-model="search"
										label="Query"
										@input="updateParams"
									/>
								</v-flex>
							</v-layout>
						</v-container>
					</v-card>
				</v-flex>
			</v-layout>
			<ArticleIterator
				:items="articles"
				:selected="selected"
				:initial-sort="{}"
				hide-toolbar
				hide-image-settings
				hide-context
				@selected="selected = $event"
				@set-report-image="setReportImage"
				@set-project-image="setProjectImage"
				@edit="setEditArticle"
			/>
			<FullDialog
				:dialog="editArticle.show"
				dialog-title="Edit Article"
				@close-dialog="editArticle.show = false"
			>
				<template
					v-if="editArticle.show"
					slot="dialog-content"
				>
					<EditArticle
						:article="editArticle.article"
						single-edit
						search-page
						@saving-done="editArticle.show = false"
						@close="editArticle.show = false"
					/>
				</template>
			</FullDialog>
			<Confirm ref="confirm"/>
		</v-container>
	</Route>
</template>

<script>
import Route from '@/views/layouts/Route';
import Debounce from 'lodash/debounce';
import { Reports } from '@/models/Report';
import { ArticlesSearch } from '@/models/Article';
import { GetCategories } from '@/models/Category';
import { Publications } from '@/models/Publication';
import ArticleIterator from '@/components/Article/ArticleIterator';
import ArticlesListMixins from '@/mixins/ArticlesListMixins';
import EditArticle from '@/components/Article/EditArticle';
import Confirm from '@/components/Confirm';
import FullDialog from '@/components/FullDialog';

export default {
	components: {
		Route,
		ArticleIterator,
		EditArticle,
		Confirm,
		FullDialog,
	},
	mixins: [ArticlesListMixins],
	data() {
		return {
			reports: [],
			reportsLoading: false,
			categories: [],
			categoriesLoading: false,
			selectedProjects: [],
			selectedReports: [],
			selectedUsers: [],
			selectedCategories: [],
			selectedPublications: [],
			hasIssues: false,
			isDeleted: false,
			search: '',
			articlesSearch: new ArticlesSearch(),
			publications: new Publications(),
			publicationsSet: [],
			publicationSearchInput: null,
			startDate: null,
			showStartDate: false,
			endDate: null,
			showEndDate: false,
		};
	},
	computed: {
		articles() {
			return this.articlesSearch.models;
		},
		projects() {
			const projects = this.$store.get('projects/active');
			if (!projects.models) {
				return [];
			}
			return projects.models.sort((a, b) => b.id - a.id);
		},
		users() {
			return this.$store.get('users/users');
		},
		publicationsItems() {
			return Array.from(this.publicationsSet);
		},
		reportsNoDataText() {
			if (this.reports.length > 0) {
				return 'No report with that name was found.';
			}
			return 'Select a project with reports first.';
		},
		categoriesNoDataText() {
			if (this.categories.length > 0) {
				return 'No category with that name was found.';
			}
			if (this.selectedProjects.length > 0) {
				return 'No categories found.';
			}
			return 'Select a project first.';
		},
		publicationsNoDataText() {
			if (this.publications.loading) {
				return 'Fetching publications...';
			}
			if (this.publicationsSet.length > 0) {
				return 'No publication with that name was found.';
			}
			return 'Type to search publications.';
		},
	},
	watch: {
		'$route.query': {
			handler() {
				this.debouncedFetch();
			},
		},
		// Fetch reports/categories for a particular project when the project is selected.
		selectedProjects(newVal, oldVal) {
			// This will run only from created() to get all reports/categories from query
			if (newVal.length > oldVal.length + 1) {
				newVal.forEach((id) => {
					this.addToReports(id);
					this.addToCategories(id);
				});
				return;
			}
			if (newVal.length > oldVal.length) {
				this.addToReports(newVal[newVal.length - 1]);
				this.addToCategories(newVal[newVal.length - 1]);
				return;
			}
			this.removeFromReports();
			this.removeFromCategories();
			this.updateParams();
		},
		selectedReports() {
			this.updateParams();
		},
		selectedUsers() {
			this.updateParams();
		},
		selectedCategories() {
			this.updateParams();
		},
		selectedPublications() {
			this.updateParams();
		},
		publicationSearchInput(newVal, oldVal) {
			if (!newVal || newVal === '') {
				return;
			}
			// Don't search if its just backspacing
			if (oldVal && newVal.length < oldVal.length) {
				return;
			}
			this.publications.set('search', newVal);
			this.debouncedPublication();
		},
		startDate(val) {
			if (!this.endDate) {
				return;
			}
			const start = Date.parse(val);
			const end = Date.parse(this.endDate);
			if (start > end) {
				this.endDate = null;
			}
		},
	},
	created() {
		this.debouncedFetch = Debounce(this.fetchArticles, 500);
		this.debouncedPublication = Debounce(this.fetchPublications, 500);
		this.setQueryFromRoute();
		this.fetchArticles();
		// publications.loading is true when instantiated but we don't want to fetch
		this.publications.loading = false;
	},
	methods: {
		fetchArticles() {
			// if (Object.keys(this.$route.query).length === 0) {
			// 	return;
			// }
			this.articlesSearch.set('params', this.$route.fullPath);
			this.articlesSearch.fetch().then().catch(() => {
				this.$notify('error', 'Failed to get articles, please try again.');
			});
		},
		fetchPublications() {
			this.publications.fetch().then(() => {
				this.publicationsSet = new Set([...this.publicationsSet, ...this.publications.models]);
			}).catch(() => {
				this.$notify('error', 'Failed to get publications');
			});
		},
		setEditArticle(article) {
			this.editArticle.article = article;
			this.editArticle.show = true;
			// Set categories for article form to use
			const categories = this.categories
				.filter(category => category.projectId === article.projectId);
			this.$store.set('categories', categories);
		},
		addToReports(projectId) {
			this.reportsLoading = true;
			const report = new Reports();
			report.set('id', parseInt(projectId, 10));
			report.fetch().then(() => {
				this.reports = [...this.reports, ...report.models.filter(o => !o.isArchived)];
				this.reports.sort((a, b) => a.displayName.localeCompare(b.displayName));
			}).catch(() => {
				this.$notify('error', 'Failed to get reports.');
			}).finally(() => {
				this.reportsLoading = false;
			});
		},
		removeFromReports() {
			this.reportsLoading = true;
			const projectIds = this.selectedProjects.map(item => parseInt(item, 10));
			for (let i = this.reports.length - 1; i >= 0; i--) {
				if (projectIds.indexOf(this.reports[i].projectId) === -1) {
					this.reports.splice(i, 1);
				}
			}
			this.reportsLoading = false;
		},
		addToCategories(projectId) {
			this.categoriesLoading = true;
			const categories = new GetCategories();
			categories.set('projectId', parseInt(projectId, 10));
			categories.fetch().then(() => {
				const filteredCategories = categories.models.filter(category => !category.isArchived);
				const catList = [];
				filteredCategories.forEach((cat) => {
					catList.push({
						id: cat.id,
						name: cat.name,
						level: cat.level,
						isArchived: cat.isArchived,
						projectId,
					});
				});
				this.categories = [...this.categories, ...catList];
			}).catch(() => {
				this.$notify('error', 'Failed to get categoires');
			}).finally(() => {
				this.categoriesLoading = false;
			});
		},
		removeFromCategories() {
			this.categoriesLoading = true;
			const projectIds = this.selectedProjects.map(item => parseInt(item, 10));
			for (let i = this.categories.length - 1; i >= 0; i--) {
				if (projectIds.indexOf(this.categories[i].projectId) === -1) {
					this.categories.splice(i, 1);
				}
			}
			this.categoriesLoading = false;
		},
		remove(item, array) {
			const index = array.indexOf(item.id);
			if (index >= 0) array.splice(index, 1);
		},
		updateParams() {
			setTimeout(() => {
				const projectIds = this.$refs.projectSearch.selectedItems.map(item => item.id);
				const reportIds = this.$refs.reportSearch.selectedItems.map(item => item.id);
				const userIds = this.selectedUsers;
				const categoryIds = this.$refs.categorySearch.selectedItems.map(item => item.id);
				const publicationIds = this.$refs.publicationSearch.selectedItems.map(item => item.id);
				// Build query hiding items that don't exist
				const query = {
					...projectIds.length > 0 && { projectIds: projectIds.join(',') },
					...reportIds.length > 0 && { reportIds: reportIds.join(',') },
					...userIds.length > 0 && { userIds: userIds.join(',') },
					...categoryIds.length > 0 && { categoryIds: categoryIds.join(',') },
					...publicationIds.length > 0 && { publisherIds: publicationIds.join(',') },
					...this.hasIssues && { hasIssues: this.hasIssues },
					...this.isDeleted && { isDeleted: this.isDeleted },
					...this.startDate && { startDate: this.startDate },
					...this.endDate && { endDate: this.endDate },
					...this.search.length > 0 && { search: this.search },
				};
				this.$router.replace({ name: 'Search', query });
			}, 0);
		},
		projectsFilter(item, queryText) {
			const contains = item.name.toLowerCase().indexOf(queryText.toLowerCase());
			if (contains > -1) {
				return true;
			}
			return false;
		},
		reportsFilter(item, queryText) {
			const contains = item.displayName.toLowerCase().indexOf(queryText.toLowerCase());
			if (contains > -1) {
				return true;
			}
			return false;
		},
		usersFilter(item, queryText) {
			const contains = item.realName.toLowerCase().indexOf(queryText.toLowerCase());
			if (contains > -1) {
				return true;
			}
			return false;
		},
		setQueryFromRoute() {
			if (this.$route.query.projectIds) {
				const projectIdsArray = this.$route.query.projectIds.split(',');
				this.selectedProjects = projectIdsArray.map(id => parseInt(id, 10));
			}
			if (this.$route.query.reportIds) {
				const reportIdsArray = this.$route.query.reportIds.split(',');
				this.selectedReports = reportIdsArray.map(id => parseInt(id, 10));
			}
			if (this.$route.query.users) {
				const usersArray = this.$route.query.users.split(',');
				this.selectedUsers = usersArray.map(id => parseInt(id, 10));
			}
			if (this.$route.query.hasIssues) {
				if (this.$route.query.hasIssues.toString() === 'true') {
					this.hasIssues = true;
				}
			}
			if (this.$route.query.isDeleted) {
				if (this.$route.query.isDeleted.toString() === 'true') {
					this.isDeleted = true;
				}
			}
			if (this.$route.query.start) {
				this.startDate = this.$route.query.start;
			}
			if (this.$route.query.end) {
				this.endDate = this.$route.query.end;
			}
		},
		validateStartDate() {
			if (this.startDate === null || this.endDate === null) {
				return true;
			}
			const start = Date.parse(this.startDate);
			const end = Date.parse(this.endDate);
			if (start > end) {
				return 'Date cannot be older than 1971';
			}
			return true;
		},
		resetStartDate() {
			if (!this.startDate) {
				return;
			}
			this.startDate = null;
			this.updateParams();
		},
		resetEndDate() {
			if (!this.endDate) {
				return;
			}
			this.endDate = null;
			this.updateParams();
		},
		resetAll() {
			this.selectedProjects = [];
			this.selectedReports = [];
			this.selectedUsers = [];
			this.selectedCategories = [];
			this.selectedPublications = [];
			this.startDate = null;
			this.endDate = null;
			this.hasIssues = false;
			this.isDeleted = false;
			this.search = '';
		},
	},
};
</script>

<style lang="scss" scoped>
.absolute--loader {
	position: absolute;
	top: 0;
	left: 0;
}
</style>
